// src/pages/PortalVerificacao.tsx
// ROTA: <Route path="verificar/:protocolo?" element={<PortalVerificacao />} />
//       <Route path="verificar"             element={<PortalVerificacao />} />

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, AlertCircle, Loader2, ArrowLeft, Shield } from 'lucide-react';
import PainelVerificacao from '../components/PortalVerificacao/PainelVerificacao';
import { consultarProtocolo, VerificacaoData, VerificacaoError } from '../services/verificacaoService';

const BRAND = { gold: '#D4AF37', charcoal: '#1F2328' };

const PortalVerificacao: React.FC = () => {
  const { protocolo: routeProtocolo } = useParams<{ protocolo?: string }>();
  const navigate = useNavigate();
  const [input,      setInput]      = useState(routeProtocolo || '');
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [dados,      setDados]      = useState<VerificacaoData | null>(null);
  const [erro,       setErro]       = useState<VerificacaoError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (routeProtocolo && routeProtocolo.length > 10) handleConsultar(routeProtocolo.trim());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProtocolo]);

  const handleConsultar = useCallback(async (proto?: string) => {
    const id = (proto ?? input).trim();
    if (!id) {
      // FIX: adicionado campo 'code' obrigatório em VerificacaoError
      setErro({ type: 'unknown', code: 'EMPTY', message: 'Informe o número do protocolo.' });
      return;
    }
    setCarregando(true); setErro(null); setDados(null);
    const { data, error } = await consultarProtocolo(id);
    setCarregando(false);
    if (error) { setErro(error); return; }
    setDados(data);
    navigate(`/verificar/${encodeURIComponent(id)}`, { replace: true });
  }, [input, navigate]);

  const handleAtualizar = useCallback(() => { if (dados?.id) handleConsultar(dados.id); }, [dados, handleConsultar]);
  const handleNovaBusca = useCallback(() => { setDados(null); setErro(null); setInput(''); navigate('/verificar', { replace: true }); setTimeout(() => inputRef.current?.focus(), 100); }, [navigate]);
  const handleExportarPDF = useCallback(async () => { setExportando(true); await new Promise<void>(r => setTimeout(r, 200)); window.print(); setExportando(false); }, []);

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: 'linear-gradient(135deg, #1F2328F0 0%, #2d2416F0 100%)', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @media print { body{background:white!important} .no-print{display:none!important} video{display:none!important} }
        @keyframes fade-in { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .fade-in { animation: fade-in 0.35s ease-out both; }
      `}</style>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8 no-print">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Shield className="w-8 h-8" style={{ color: BRAND.gold }} />
            <span className="text-2xl font-black text-white tracking-tight">Portal de Verificação</span>
          </div>
          <p className="text-sm text-gray-300">Consulte o status da sua solicitação de filiação</p>
        </div>

        {!dados && (
          <div className="rounded-3xl p-8 shadow-2xl fade-in no-print" style={{ backgroundColor: 'rgba(255,255,255,0.97)' }}>
            <h2 className="text-2xl font-black mb-2" style={{ color: BRAND.charcoal }}>Informe seu Protocolo</h2>
            <p className="text-sm text-gray-500 mb-6">O número foi gerado ao finalizar seu cadastro de filiação.</p>
            <div className="flex gap-3">
              <input
                ref={inputRef} type="text" value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConsultar()}
                placeholder="Ex: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="flex-1 px-5 py-4 rounded-2xl border text-sm font-mono outline-none focus:ring-2 transition"
                style={{ borderColor: '#D4AF3755', backgroundColor: '#fafaf9', color: BRAND.charcoal }}
                autoFocus
              />
              <button onClick={() => handleConsultar()} disabled={carregando}
                className="px-6 py-4 rounded-2xl font-black text-white transition hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: BRAND.charcoal }}
              >
                {carregando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                <span className="hidden sm:inline">Consultar</span>
              </button>
            </div>
            {erro && (
              <div className="mt-4 flex items-center gap-3 p-4 rounded-2xl border" style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 font-medium">{erro.message}</p>
              </div>
            )}
            <p className="text-xs text-gray-400 text-center mt-6">Dados protegidos · Nenhuma informação pessoal armazenada neste acesso</p>
          </div>
        )}

        {carregando && !dados && (
          <div className="flex flex-col items-center justify-center py-16 text-white gap-4">
            <Loader2 className="w-10 h-10 animate-spin" style={{ color: BRAND.gold }} />
            <p className="font-bold text-lg">Consultando protocolo...</p>
          </div>
        )}

        {dados && (
          <div className="fade-in">
            <button onClick={handleNovaBusca} className="flex items-center gap-2 text-sm text-white/80 hover:text-white mb-5 transition no-print">
              <ArrowLeft className="w-4 h-4" /> Nova consulta
            </button>
            <PainelVerificacao data={dados} protocolo={input || dados.id} onExportarPDF={handleExportarPDF} onAtualizar={handleAtualizar} exportando={exportando} />
          </div>
        )}
      </div>
    </div>
  );
};
export default PortalVerificacao;